export type ReasoningEffort = 'low' | 'medium' | 'high' | null;

export type ModelStrategyChannelKey = 'default' | 'chat' | 'voice' | 'email';

/**
 * Client-side bucket label. The backend stores buckets as XOR over the
 * selector fields (see `language_model_id` / `language_model_alias`); the
 * label is derived, never persisted.
 */
export type ModelStrategyBucket = 'internal' | 'proxy' | 'external';

/**
 * A single step in a channel's fallback chain. Exactly one bucket is
 * selected per step:
 *  - `internal` → both selectors `null`.
 *  - `proxy`    → `language_model_alias` set, `language_model_id` null.
 *  - `external` → `language_model_id` set, `language_model_alias` null.
 */
export type ModelStrategyStep = {
  language_model_id: string | null;
  language_model_alias: string | null;
  reasoning_effort: ReasoningEffort;
};

/**
 * Brain-scoped strategy as exchanged over the wire. Channel is the JSON key;
 * array index is the position. Empty array for a per-channel chain means
 * "inherit the default chain at runtime."
 */
export type ModelStrategy = {
  default: ModelStrategyStep[];
  chat: ModelStrategyStep[];
  voice: ModelStrategyStep[];
  email: ModelStrategyStep[];
};

/**
 * Catalog entry as returned by the orchestrator. All buckets share the same
 * flat shape — distinguishable by `bucket` and by whether
 * `language_model_id` is populated (external) or null (internal/proxy).
 *
 *  - `internal` → Moveo first-party model (e.g. `moveo-maestro`). Selecting
 *                 it persists `language_model_alias: <alias>`.
 *  - `proxy`    → Third-party chat model exposed via the platform proxy.
 *                 Persists `language_model_alias: <alias>`.
 *  - `external` → BYOLLM row owned by the account. Persists
 *                 `language_model_id: <uuid>`.
 */
export type ModelCatalogEntry = {
  bucket: ModelStrategyBucket;
  alias: string;
  provider: string;
  language_model_id: string | null;
  max_input_tokens: number | null;
  max_output_tokens: number | null;
  supports_reasoning: boolean;
  supports_function_calling: boolean;
  supports_vision: boolean;
};

export type ModelCatalog = { models: ModelCatalogEntry[] };

export const MODEL_STRATEGY_CHANNEL_KEYS: ModelStrategyChannelKey[] = [
  'default',
  'chat',
  'voice',
  'email',
];

// Voice is intentionally last — it's the only single-step channel (no fallback).
export const PER_CHANNEL_KEYS: Exclude<ModelStrategyChannelKey, 'default'>[] = [
  'chat',
  'email',
  'voice',
];

export const REASONING_EFFORT_OPTIONS = ['low', 'medium', 'high'] as const;

/** Backend cap; valid positions are `[0, MAX_STEPS_PER_CHAIN - 1]`. */
export const MAX_STEPS_PER_CHAIN = 3;

export const MODEL_STRATEGY_ERROR_CODES = {
  chainFull: 'model-strategy-chain-full',
  bucketXor: 'model-strategy-bucket-xor',
  unknownAlias: 'model-strategy-unknown-alias',
} as const;

export type ModelStrategyErrorCode =
  (typeof MODEL_STRATEGY_ERROR_CODES)[keyof typeof MODEL_STRATEGY_ERROR_CODES];

/**
 * Structured validation error returned by `PATCH /model-strategy`. `channel`
 * and `position` are present when the error attributes to a specific row.
 */
export type ModelStrategyValidationError = {
  code?: ModelStrategyErrorCode | string;
  channel?: ModelStrategyChannelKey;
  position?: number;
  message?: string;
};

export const emptyStep = (): ModelStrategyStep => ({
  language_model_id: null,
  language_model_alias: null,
  reasoning_effort: null,
});

/** A step with no model picked yet, like the one `emptyStep` adds. */
export const isEmptyStep = (step: ModelStrategyStep) =>
  !step.language_model_id && !step.language_model_alias;

export const emptyStrategy = (): ModelStrategy => ({ default: [], chat: [], voice: [], email: [] });

/** The chain a brain runs before it ever saves a strategy; mirrors the editor's seed. */
export const legacyDefaultChain = (
  languageModelId: string | null | undefined
): ModelStrategyStep[] => [
  languageModelId
    ? { language_model_id: languageModelId, language_model_alias: null, reasoning_effort: null }
    : emptyStep(),
];

/** Classify a step into its bucket (XOR over selector fields). */
export const stepBucket = (step: ModelStrategyStep): ModelStrategyBucket => {
  if (step.language_model_id && step.language_model_alias) {
    // Backend enforces XOR via `model-strategy-bucket-xor`; surface violations
    // in dev so we don't silently mask a server bug.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[modelStrategy] step violates XOR; classifying as external', step);
    }
    return 'external';
  }
  if (step.language_model_id) {
    return 'external';
  }
  if (step.language_model_alias) {
    return 'proxy';
  }
  return 'internal';
};

/**
 * Uniqueness key for a step, mirroring the orchestrator's `.unique()` rule:
 * a chain can't repeat the same model. Alias and BYOLLM id are distinct
 * namespaces; a both-null step keys to `internal` (only one allowed).
 */
export const stepSelectorKey = (
  step: Pick<ModelStrategyStep, 'language_model_id' | 'language_model_alias'>
): string => {
  if (step.language_model_alias) {
    return step.language_model_alias;
  }
  if (step.language_model_id) {
    return `id:${step.language_model_id}`;
  }
  return 'internal';
};

export const sameStepContent = (a: ModelStrategyStep, b: ModelStrategyStep): boolean =>
  a.language_model_id === b.language_model_id &&
  a.language_model_alias === b.language_model_alias &&
  a.reasoning_effort === b.reasoning_effort;

/** Structural per-channel compare; avoids pulling in lodash for one equality check. */
export const strategiesEqual = (a: ModelStrategy, b: ModelStrategy): boolean =>
  MODEL_STRATEGY_CHANNEL_KEYS.every((channel) => {
    const x = a[channel] ?? [];
    const y = b[channel] ?? [];
    return x.length === y.length && x.every((step, i) => sameStepContent(step, y[i]));
  });
